import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import { useSelector } from 'react-redux'
import { selectState } from '../store/slices/themeSlice'

interface ColumnInfoProps {
  /** Column label, used for the screen-reader name ("About Status"). */
  label: string
  /** The explanation shown in the tooltip. */
  description: string
}

const TOOLTIP_WIDTH = 260
const GAP = 8

/** Small ⓘ next to a table header label that explains the column, in the
 * primary button colors. Opens on
 * hover, keyboard focus or click/tap; clicks don't reach the header (so they
 * don't sort the column). The tooltip is rendered in a portal with fixed
 * positioning so the tables' scroll wrappers can't clip it. */
const ColumnInfo: React.FC<ColumnInfoProps> = ({ label, description }) => {
  // Same colors as the primary button (CustomButtonStyles), so it follows the
  // business's white-label theme.
  const { theme } = useSelector(selectState)
  const buttonColors = theme.colorSchema.buttons.primary
  const iconRef = useRef<HTMLSpanElement>(null)
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const open = hovered || pinned

  const updatePosition = useCallback(() => {
    const rect = iconRef.current?.getBoundingClientRect()
    if (!rect) return
    const left = Math.min(
      Math.max(GAP, rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2),
      window.innerWidth - TOOLTIP_WIDTH - GAP,
    )
    setPosition({ top: rect.bottom + GAP, left })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePosition()
    const close = () => {
      setHovered(false)
      setPinned(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    const onPointerDown = (e: PointerEvent) => {
      if (!iconRef.current?.contains(e.target as Node)) close()
    }
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, updatePosition])

  const tooltipId = `column-info-${label.replace(/\W+/g, '-').toLowerCase()}`

  return (
    <>
      <span
        ref={iconRef}
        role="button"
        tabIndex={0}
        aria-label={`About ${label}`}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          marginLeft: 6,
          cursor: 'help',
          opacity: open ? 1 : 0.6,
          verticalAlign: 'middle',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => {
          setHovered(false)
          setPinned(false)
        }}
        onClick={(e) => {
          e.stopPropagation()
          setPinned((p) => !p)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            setPinned((p) => !p)
          }
        }}
      >
        <Info size={14} aria-hidden="true" />
      </span>
      {open &&
        position &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: TOOLTIP_WIDTH,
              zIndex: 10000,
              padding: '8px 10px',
              borderRadius: 6,
              background: `linear-gradient(to right, ${buttonColors.background[0]}, ${buttonColors.background[1]})`,
              color: buttonColors.text,
              fontSize: 13,
              lineHeight: 1.4,
              fontWeight: 400,
              fontFamily: 'inherit',
              textAlign: 'left',
              textTransform: 'none',
              whiteSpace: 'normal',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              pointerEvents: 'none',
            }}
          >
            {description}
          </div>,
          document.body,
        )}
    </>
  )
}

export default ColumnInfo
